/**
 * EF-CLIENT-PORTAL-V1-STEP3: Shift Detail Modal
 * 
 * Read-only modal showing shift details:
 * - Shift name, date, time
 * - Meeting point (venue location)
 * - Notes/instructions
 * - Assigned crew
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  User,
  FileText,
  Building,
  Navigation,
} from "lucide-react";

/**
 * Format date for display
 */
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Get status badge color
 */
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "active":
    case "confirmed":
    case "published":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "draft":
      return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    case "completed":
      return "bg-blue-500/10 text-blue-500 border-blue-500/20";
    case "cancelled":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export default function ShiftDetailModal({ shift, open, onOpenChange }) {
  if (!shift) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="shift-detail-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-lg font-semibold pr-8">
              {shift.name}
            </DialogTitle>
            <Badge
              variant="outline"
              className={`shrink-0 ${getStatusColor(shift.status)}`}
            >
              {shift.status}
            </Badge>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-5 pr-2">
            {/* Date & Time */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Schedule
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="text-sm">{formatDate(shift.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm">
                    {shift.start_time}
                    {shift.end_time && ` - ${shift.end_time}`}
                    {shift.duration_hours && (
                      <span className="text-muted-foreground ml-1">
                        ({shift.duration_hours}h)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Location
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Building className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">{shift.venue_name}</p>
                    {shift.venue_address && (
                      <p className="text-xs text-muted-foreground">
                        {shift.venue_address}
                      </p>
                    )}
                  </div>
                </div>
                {shift.venue_location_name && (
                  <div className="flex items-center gap-2 pl-6">
                    <Navigation className="h-3.5 w-3.5 text-primary" />
                    <span className="text-sm">
                      <span className="text-muted-foreground">Meeting Point: </span>
                      <span className="font-medium">{shift.venue_location_name}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* EF-SHIFT-MEETING-PLACE-V1: Meeting Place Instructions */}
            {shift.meeting_place && (
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  Meeting Place
                </h4>
                <div className="bg-primary/5 border border-primary/20 rounded-md p-3">
                  <p className="text-sm whitespace-pre-wrap">{shift.meeting_place}</p>
                </div>
              </div>
            )}

            {/* Notes/Instructions */}
            {shift.notes && (
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Instructions
                </h4>
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-primary mt-0.5" />
                  <p className="text-sm whitespace-pre-wrap">{shift.notes}</p>
                </div>
              </div>
            )}

            {/* Assigned Crew */}
            <div className="space-y-3">
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Assigned Crew ({shift.staff_assigned || 0}/{shift.staff_required || 0})
              </h4>
              {shift.assigned_crew?.length > 0 ? (
                <div className="space-y-2">
                  {shift.assigned_crew.map((crew) => (
                    <div
                      key={crew.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-accent/50"
                      data-testid={`crew-${crew.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <span className="text-sm font-medium">{crew.name}</span>
                      </div>
                      {crew.role && (
                        <Badge variant="secondary" className="text-xs">
                          {crew.role}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  <Users className="h-5 w-5 mx-auto mb-1 opacity-50" />
                  No crew assigned yet
                </div>
              )}
            </div>

            {/* Project Link */}
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Project: <span className="text-primary">{shift.project_name}</span>
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
